/**
 * Swiper carousel
 * https://swiperjs.com/
 */

// import Swiper and modules styles
import Swiper from 'swiper';
import { Navigation, Pagination, Keyboard, A11y, EffectFade } from 'swiper/modules';
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import 'swiper/css/keyboard'
import 'swiper/css/a11y';

export function swiper() {
  if (jQuery(".swiper").length === 0) {
    return;
  }

  function initSwipers(defaults = {}, selector = ".swiper") {
    let swipers = document.querySelectorAll(selector);
  
    swipers.forEach((swiperContainer, index) => {
      // Create a unique class name for the swiper container
      let swiperClass = 'swiper-' + index;
  
      // Add the unique class to the swiper container if not already added
      if (!swiperContainer.classList.contains(swiperClass)) {
        swiperContainer.classList.add(swiperClass);
  
        // Get options
        let optionsData = swiperContainer.dataset.swiper
          ? JSON.parse(swiperContainer.dataset.swiper)
          : {};

        // Get dottext from data attribute
        let dottext = swiperContainer.getAttribute('data-dottext') || 'Gå til slide #';

        // Check if the swiper should be active only on mobile
        let shouldEnableSwiper = optionsData.mobileOnly && window.innerWidth <= 768;

        // Combine defaults and custom options
        let options = {
          ...defaults,
          ...optionsData,
          navigation: {
            nextEl: swiperContainer.querySelector('.swiper-button-next'),
            prevEl: swiperContainer.querySelector('.swiper-button-prev'),
          }, // Set navigation options specific to this swiper
          pagination: {
            ...defaults.pagination,
            el: swiperContainer.querySelector('.swiper-pagination'),
            renderBullet: function (index, className) {
              let ariaLabel = dottext.replace('#', index + 1);
              // swiper-pagination-bullet class is on <button>, not <li>
              // Swiper's A11y manages tabindex on the button only — <li> is never focusable
              return `<li><button type="button" class="${className}" aria-label="${ariaLabel}"></button></li>`;
            },
          },
        };

        // Initialize Swiper only if it should be active on the current device
        if (!optionsData.mobileOnly || shouldEnableSwiper) {

          // Initialize Swiper
          let swiperInstance = new Swiper(swiperContainer, options);

          // Carousel semantics (APG) — a11y module doesn't set role=region itself
          swiperContainer.setAttribute('role', 'region');

          // Keyboard nav should only hijack arrow/PageUp/PageDown keys while
          // focus is actually inside the slider, not just when it's in the viewport
          swiperInstance.keyboard.disable();
          swiperContainer.addEventListener('focusin', () => swiperInstance.keyboard.enable());
          swiperContainer.addEventListener('focusout', (e) => {
            if (!swiperContainer.contains(e.relatedTarget)) {
              swiperInstance.keyboard.disable();
            }
          });

          // Inactive slides must not be reachable/announced (effect: fade keeps them at opacity:0 only).
          // Use activeIndex, not the swiper-slide-active class: Swiper moves that class only after
          // 'slideChange', and loop mode reorders the slides, so the class check made the visible slide inert.
          function updateInertSlides() {
            const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
            // Focus inside the slide that becomes inert (e.g. arrow keys change the slide) would drop to <body>
            const focused = document.activeElement;
            const focusLeavesSlide = focused && !activeSlide.contains(focused)
              && swiperInstance.slides.some(slide => slide.contains(focused));

            swiperInstance.slides.forEach(slide => {
              slide.toggleAttribute('inert', slide !== activeSlide);
            });

            if (focusLeavesSlide) {
              // Move it to the new slide's first link/button, or to the active pagination bullet
              const target = activeSlide.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
                || swiperContainer.querySelector('.swiper-pagination-bullet-active');
              if (target) {
                target.focus({ preventScroll: true });
              }
            }
          }
          updateInertSlides();
          swiperInstance.on('slideChange activeIndexChange loopFix transitionEnd', updateInertSlides);

          // Label the pagination container so screen readers announce "Slide-navigation, list, X items"
          const paginationContainer = swiperContainer.querySelector('.swiper-pagination');
          if (paginationContainer && !paginationContainer.getAttribute('aria-label')) {
            paginationContainer.setAttribute('aria-label', 'Slide-navigation');
          }

            // Remove aria-live from swiper-wrapper — Swiper's A11y adds it, but it causes
          // every DOM change inside slides (e.g. lazy images) to be announced unnecessarily.
          const wrapper = swiperContainer.querySelector('.swiper-wrapper');
          if (wrapper) wrapper.removeAttribute('aria-live');

          // Swiper's own A11y module (v10.3.1) only ever calls its internal notify()
          // from a keydown handler on the prev/next buttons — grep node_modules/swiper/
          // modules/a11y.mjs and the only `notify(` call site is inside onEnterOrSpaceKey.
          // It is never invoked for click, swipe, pagination-bullet activation, or autoplay,
          // so .swiper-notification is otherwise permanently silent. We drive it ourselves
          // via the 'slideChange' listener below, which fires for all of those triggers.
          //
          // Set up the live region's attributes ONCE, right after init, rather than inside
          // the slideChange handler: re-declaring aria-live/role alongside a content change
          // on every update is a known trap — some screen readers treat that as the region
          // being newly created and drop the announcement instead of reading the new text.
          const notifEl = swiperContainer.querySelector('.swiper-notification');
          if (notifEl) {
            notifEl.setAttribute('role', 'status');
            notifEl.setAttribute('aria-live', 'polite');
            notifEl.setAttribute('aria-atomic', 'true');
          }

          // Pagination click — Swiper's built-in handler loses track of slide index when
          // renderBullet wraps bullets in <li>. We handle it ourselves.
          const paginationEl = swiperContainer.querySelector('.swiper-pagination');
          if (paginationEl) {
            paginationEl.addEventListener('click', function(e) {
              const btn = e.target.closest('button.swiper-pagination-bullet');
              if (!btn) return;
              const bullets = Array.from(paginationEl.querySelectorAll('button.swiper-pagination-bullet'));
              const index = bullets.indexOf(btn);
              if (index !== -1) swiperInstance.slideToLoop(index);
            });
          }

          // Keyboard activation for prev/next — they are <div role="button"> so browsers
          // do not fire click on Enter/Space automatically.
          ['.swiper-button-prev', '.swiper-button-next'].forEach(sel => {
            const el = swiperContainer.querySelector(sel);
            if (!el) return;
            el.addEventListener('keydown', function(e) {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                el.click();
              }
            });
          });

          // Status announcement — fires on every index change regardless of trigger
          // (arrow click, swipe, pagination-bullet click, autoplay), unlike Swiper's own
          // notify(). Only the text is touched here; role/aria-live/aria-atomic are set
          // once above and left alone.
          swiperInstance.on('slideChange', function() {
            const liveNotifEl = swiperContainer.querySelector('.swiper-notification');
            if (!liveNotifEl) return;

            const current = swiperInstance.realIndex + 1;
            const bullets = swiperContainer.querySelectorAll('.swiper-pagination button.swiper-pagination-bullet');
            const total = bullets.length
              || swiperContainer.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;

            // Clear then set on a short delay — covers the case where the new text is
            // identical to what's already there (e.g. loop wraparound landing back on the
            // same realIndex), which some screen readers won't announce as a change
            // otherwise. A single requestAnimationFrame (~16ms) is too tight for reliable
            // detection across browser/AT combinations; 100ms is the established pattern
            // for accessible live-region updates.
            liveNotifEl.textContent = '';
            setTimeout(() => {
              liveNotifEl.textContent = `Slide ${current} / ${total}`;
            }, 100);
          });
        }
      }
    });
  }  

  // Basic options for all sliders
  let defaults = {
    modules: [Navigation, Pagination, Keyboard, A11y, EffectFade],
    slidesPerView: 1,
    spaceBetween: 30,
    autoHeight: false,
    effect: "fade",
    loop: true,
    lazy: true,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    keyboard: {
      enabled: true,
      pageUpDown: false, // was hijacking page scroll
    },
    a11y: {
      enabled: true,
      prevSlideMessage: 'Forrige slide',
      nextSlideMessage: 'Næste slide',
      firstSlideMessage: 'Dette er det første slide',
      lastSlideMessage: 'Dette er det sidste slide',
      paginationBulletMessage: 'Gå til slide {{index}}',
      containerMessage: 'Karrusel',
      containerRoleDescriptionMessage: 'karrusel',
      itemRoleDescriptionMessage: 'slide',
    },
  };
  
  // Call the init function
  initSwipers(defaults);

}

export default swiper;
